import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { Bell } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@org/ui';
import {
  NOTIFICATION_ICONS,
  NOTIFICATION_COLORS,
  NOTIFICATION_I18N_KEYS,
  getNotificationLink,
  type NotificationType,
} from './notification-presentation';

type NotificationLike = {
  notificationId: string;
  type: string;
  payload: Record<string, unknown> | null;
  createdAt: Date | string;
};

export function NotificationToast({
  notification,
  onClick,
}: {
  notification: NotificationLike;
  onClick: () => void;
}) {
  const { t } = useTranslation('community');
  const type = notification.type as NotificationType;
  const Icon = NOTIFICATION_ICONS[type] ?? Bell;
  const color = NOTIFICATION_COLORS[type] ?? 'text-muted-foreground';
  const data = notification.payload as Record<string, unknown> | null;
  const actorName = (data?.actorName as string | undefined) ?? undefined;
  const postTitle = (data?.postTitle as string | undefined) ?? undefined;
  const badge = (data?.badgeName as string | undefined) ?? undefined;
  const level = (data?.level as number | undefined) ?? undefined;
  const link = getNotificationLink(type, data);

  return (
    <Link
      to={link}
      onClick={onClick}
      className="flex w-[360px] items-start gap-3 rounded-lg border bg-background p-3 shadow-lg transition-colors hover:bg-muted/40"
    >
      <div className={`mt-0.5 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          {actorName && <span className="font-medium">{actorName} </span>}
          {t(NOTIFICATION_I18N_KEYS[type] ?? 'notifications.newNotification', {
            badge,
            level,
          })}
        </p>
        {postTitle && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">"{postTitle}"</p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
        </p>
      </div>
    </Link>
  );
}

export function MessageToast({
  partnerId,
  actorName,
  preview,
  avatarUrl,
  onClick,
}: {
  partnerId: string;
  actorName: string;
  preview: string;
  avatarUrl?: string | null;
  onClick: () => void;
}) {
  const { t } = useTranslation('community');
  const initial = (actorName.trim().slice(0, 1) || '?').toUpperCase();

  return (
    <Link
      to="/messages/$partnerId"
      params={{ partnerId }}
      onClick={onClick}
      className="flex w-[360px] items-start gap-3 rounded-lg border bg-background p-3 shadow-lg transition-colors hover:bg-muted/40"
    >
      <Avatar className="h-9 w-9">
        <AvatarImage src={avatarUrl ?? undefined} />
        <AvatarFallback className="bg-primary/10 text-primary">{initial}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          {t('messages.sentYouAMessage', { actor: actorName })}
        </p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{preview}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('messages.justNow', { defaultValue: 'Just now' })}
        </p>
      </div>
    </Link>
  );
}
